import React, { createContext, useContext, useReducer, useEffect, useCallback, useRef } from 'react';
import { apiClient, type User, type LoginRequest, type RegisterRequest } from '../services/api/apiClient';
import subscriptionService, {
  type UserSubscription,
  type ContentAccess,
  type RequirementStatus,
  type PlanFamily
} from '../services/payment/subscriptionService';
import { invalidateFeatureAccessCache } from '../hooks/useFeatureAccess';

// Use User type from API service - no need to redefine

interface SubscriptionState {
  subscriptions: UserSubscription[];
  hasFcmsAccess: boolean;
  hasBitcoinAccess: boolean;
  /** Mind. ein zahlpflichtiges Pflicht-Abo aktiv. Admins immer true. */
  hasRequiredSubscription: boolean;
  /** Pflicht-Familien, in denen der Nutzer aktiv ist. */
  activeFamilies: PlanFamily[];
  /** Voller Pflicht-Abo-Status inkl. verfügbarer Onboarding-Plans (für die Pflicht-Seite). */
  requirement: RequirementStatus | null;
  isLoadingSubscriptions: boolean;
  /** True, sobald der erste refreshSubscriptions-Call durch ist (egal ob success oder error). Verhindert Race-Condition beim Page-Refresh. */
  subscriptionsInitialized: boolean;
}

interface AuthState {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  error: string | null;
  originalRole: string | null; // Speichert die ursprüngliche Rolle für Admin-Rollenwechsel
  selectedRole: string | null; // Speichert die vom Admin gewählte Rolle (für Testing)
  // Subscription state
  subscriptions: UserSubscription[];
  hasFcmsAccess: boolean;
  hasBitcoinAccess: boolean;
  hasRequiredSubscription: boolean;
  activeFamilies: PlanFamily[];
  requirement: RequirementStatus | null;
  isLoadingSubscriptions: boolean;
  subscriptionsInitialized: boolean;
}

type AuthAction =
  | { type: 'LOGIN_START' }
  | { type: 'LOGIN_SUCCESS'; payload: { user: User; originalRole?: string } }
  | { type: 'LOGIN_FAILURE'; payload?: string }
  | { type: 'LOGOUT' }
  | { type: 'UPDATE_USER'; payload: User }
  | { type: 'SET_ERROR'; payload: string }
  | { type: 'CLEAR_ERROR' }
  | { type: 'AUTH_CHECK_COMPLETE' }
  | { type: 'SET_SELECTED_ROLE'; payload: string | null }
  // Subscription actions
  | { type: 'SUBSCRIPTIONS_LOADING' }
  | { type: 'SUBSCRIPTIONS_LOADED'; payload: { subscriptions: UserSubscription[]; hasFcmsAccess: boolean; hasBitcoinAccess: boolean; hasRequiredSubscription: boolean; requirement: RequirementStatus | null } }
  | { type: 'SUBSCRIPTIONS_ERROR' };

// Context
interface AuthContextType extends AuthState {
  login: (credentials: LoginRequest) => Promise<{ success: boolean }>;
  loginWithToken: (token: string) => Promise<{ success: boolean }>;
  logout: () => Promise<void>;
  register: (data: RegisterRequest) => Promise<{ success: boolean; requiresVerification?: boolean }>;
  updateUser: (user: User) => void;
  clearError: () => void;
  refreshUserProfile: () => Promise<void>;
  originalRole: string | null; // Expose original role for UI components
  setDemoRole: (role: 'admin' | 'beta' | 'user') => void;
  // Subscription methods
  refreshSubscriptions: () => Promise<void>;
  checkContentAccess: (assetSymbol?: string) => Promise<ContentAccess>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Reducer
const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'LOGIN_START':
      return { ...state, isLoading: true, error: null };
    
    case 'LOGIN_SUCCESS':
      // Use the original role if provided (for restore), otherwise use user's role
      const origRole = action.payload.originalRole || action.payload.user.role;
      
      // Check if there's a persisted selected role for admin users
      const persistedRole = origRole === 'admin' 
        ? localStorage.getItem('admin_selected_role') 
        : null;
      
      // Demo-Rolle anwenden: role + isBetaTester
      let userWithDemoRole = action.payload.user;
      if (persistedRole && persistedRole !== 'admin') {
        userWithDemoRole = {
          ...action.payload.user,
          role: 'user' as const,
          isBetaTester: persistedRole === 'beta',
        };
      }

      return {
        ...state,
        isLoading: false,
        isAuthenticated: true,
        user: userWithDemoRole,
        originalRole: origRole,
        selectedRole: persistedRole,
        error: null,
      };
    
    case 'LOGIN_FAILURE':
      return {
        ...state,
        isLoading: false,
        isAuthenticated: false,
        user: null,
        error: action.payload || 'Login fehlgeschlagen',
      };
    
    case 'LOGOUT':
      // Clear selected role from localStorage on logout
      localStorage.removeItem('admin_selected_role');
      return {
        ...state,
        isAuthenticated: false,
        user: null,
        originalRole: null, // Lösche auch originalRole bei Logout
        selectedRole: null,
        isLoading: false,
        error: null,
        // Clear subscription state
        subscriptions: [],
        hasFcmsAccess: false,
        hasBitcoinAccess: false,
        hasRequiredSubscription: false,
        activeFamilies: [],
        requirement: null,
        isLoadingSubscriptions: false,
        subscriptionsInitialized: false,
      };
    
    case 'UPDATE_USER':
      return {
        ...state,
        user: action.payload,
      };
    
    case 'SET_ERROR':
      return {
        ...state,
        error: action.payload,
      };
    
    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null,
      };
    
    case 'SET_SELECTED_ROLE':
      // Persist selected role to localStorage
      if (action.payload) {
        localStorage.setItem('admin_selected_role', action.payload);
      } else {
        localStorage.removeItem('admin_selected_role');
      }
      return {
        ...state,
        selectedRole: action.payload,
      };
    
    case 'AUTH_CHECK_COMPLETE':
      return {
        ...state,
        isLoading: false,
      };

    // Subscription actions
    case 'SUBSCRIPTIONS_LOADING':
      return {
        ...state,
        isLoadingSubscriptions: true,
      };

    case 'SUBSCRIPTIONS_LOADED':
      return {
        ...state,
        isLoadingSubscriptions: false,
        subscriptionsInitialized: true,
        subscriptions: action.payload.subscriptions,
        hasFcmsAccess: action.payload.hasFcmsAccess,
        hasBitcoinAccess: action.payload.hasBitcoinAccess,
        hasRequiredSubscription: action.payload.hasRequiredSubscription,
        requirement: action.payload.requirement,
        activeFamilies: action.payload.requirement?.activeFamilies ?? [],
      };

    case 'SUBSCRIPTIONS_ERROR':
      return {
        ...state,
        isLoadingSubscriptions: false,
        subscriptionsInitialized: true,
        subscriptions: [],
        hasFcmsAccess: false,
        hasBitcoinAccess: false,
        hasRequiredSubscription: false,
        activeFamilies: [],
        requirement: null,
      };

    default:
      return state;
  }
};

// Initial state
const initialState: AuthState = {
  user: null,
  isLoading: true, // Start with loading true to prevent premature redirects
  isAuthenticated: false,
  error: null,
  originalRole: null,
  selectedRole: null,
  // Subscription state
  subscriptions: [],
  hasFcmsAccess: false,
  hasBitcoinAccess: false,
  hasRequiredSubscription: false,
  activeFamilies: [],
  requirement: null,
  isLoadingSubscriptions: false,
  subscriptionsInitialized: false,
};

// Provider
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // State-Ref, damit useCallback-Funktionen (refreshUserProfile etc.) eine
  // stabile Identitaet behalten und nicht bei jedem State-Update neu erzeugt
  // werden -- sonst entstehen Endlosschleifen, wenn Konsumenten diese
  // Funktionen in useEffect-Dependencies eintragen.
  const stateRef = useRef(state);
  stateRef.current = state;

  // Restore auth on app start
  useEffect(() => {
    const restoreAuth = async () => {
      // Don't dispatch LOGIN_START here since isLoading already starts as true

      try {
        // Token-Quellen-Reihenfolge:
        //   1. localStorage (Remember Me / Standard-Login)
        //   2. sessionStorage (temporäre Sitzung)
        //   3. HttpOnly-Cookie auth_token (gesetzt vom verify-email-Redirect
        //      nach Kombi-Checkout) — wird vom Browser automatisch mitgesendet,
        //      weil apiClient withCredentials=true hat. Wir können den Cookie
        //      hier nicht lesen (HttpOnly), aber den getCurrentUser-Call
        //      trotzdem versuchen — Backend liest ihn über extractToken().
        const storedToken = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
        const hasCookieAuth = typeof document !== 'undefined' && document.cookie.split(';').some(c => c.trim().startsWith('rf_session='));
        const shouldTry = !!storedToken || hasCookieAuth;
        if (shouldTry) {
          const response = await apiClient.getCurrentUser();
          if (response.success && response.data?.user) {
            // Validate that we have a valid user ID (email may be empty due to encryption)
            const user = response.data.user;
            if (user.id && user.firstName && user.lastName) {
              // Check for persisted selected role if admin
              const persistedRole = user.role === 'admin' 
                ? localStorage.getItem('admin_selected_role') 
                : null;
              
              // Apply persisted demo role if exists
              const userWithRole = persistedRole && persistedRole !== 'admin'
                ? { ...user, role: 'user' as const, isBetaTester: persistedRole === 'beta' }
                : user;
              
              // Pass both the modified user and the original role
              dispatch({ type: 'LOGIN_SUCCESS', payload: { user: userWithRole, originalRole: user.role } });
              
              // Also fetch fresh profile data with decrypted values
              try {
                const profileResponse = await apiClient.getUserProfile();
                if (profileResponse.success && profileResponse.data) {
                  const updatedUser = {
                    ...userWithRole,
                    firstName: profileResponse.data.firstName,
                    lastName: profileResponse.data.lastName,
                    email: profileResponse.data.email,
                    // emailVerified-Spiegelung — siehe refreshUserProfile-Kommentar.
                    isEmailVerified: profileResponse.data.emailVerified ?? profileResponse.data.isEmailVerified ?? userWithRole.isEmailVerified
                  };
                  dispatch({ type: 'UPDATE_USER', payload: updatedUser });
                }
              } catch (err) {
                console.log('Could not fetch fresh profile data:', err);
              }
            } else {
              console.error('Invalid user data received:', user);
              // Clear corrupted user data
              localStorage.removeItem('auth_token');
              sessionStorage.removeItem('auth_token');
              localStorage.removeItem('user_data');
              localStorage.removeItem('auth_user');
              dispatch({ type: 'AUTH_CHECK_COMPLETE' }); // Just mark as complete, don't change auth state
            }
          } else {
            // Clear invalid/expired token
            localStorage.removeItem('auth_token');
            sessionStorage.removeItem('auth_token');
            localStorage.removeItem('user_data');
            localStorage.removeItem('auth_user');
            dispatch({ type: 'AUTH_CHECK_COMPLETE' }); // Just mark as complete
          }
        } else {
          // No token = not logged in
          dispatch({ type: 'AUTH_CHECK_COMPLETE' }); // Just mark as complete
        }
      } catch (error) {
        console.error('Auth restoration failed:', error);
        // Clear any corrupted auth data
        localStorage.removeItem('auth_token');
        sessionStorage.removeItem('auth_token');
        localStorage.removeItem('user_data');
        localStorage.removeItem('auth_user');
        dispatch({ type: 'AUTH_CHECK_COMPLETE' }); // Just mark as complete
      }
    };

    restoreAuth();
  }, []);

  const login = async (credentials: LoginRequest): Promise<{ success: boolean }> => {
    try {
      dispatch({ type: 'LOGIN_START' });

      const response = await apiClient.login(credentials);

      if (response.success && response.data?.user) {
        // No originalRole needed here as it will use user.role by default
        dispatch({ type: 'LOGIN_SUCCESS', payload: { user: response.data.user } });
        return { success: true };
      } else {
        dispatch({ type: 'LOGIN_FAILURE', payload: response.error });
        return { success: false };
      }
    } catch (error: any) {
      dispatch({ type: 'LOGIN_FAILURE', payload: error.message || 'Login fehlgeschlagen' });
      // Re-throw error to preserve response data for component
      throw error;
    }
  };

  // Wird nach Passkey-Login aufgerufen: Token persistieren, User vom Backend laden,
  // State synchron zum Token bringen, damit ProtectedRoute beim navigate() bereits
  // isAuthenticated=true sieht und nicht zurück zur Login-Seite redirectet.
  const loginWithToken = async (token: string): Promise<{ success: boolean }> => {
    try {
      dispatch({ type: 'LOGIN_START' });

      // Passkeys sind an das Gerät gebunden -> Token bleibt persistent (localStorage).
      localStorage.setItem('auth_token', token);
      sessionStorage.removeItem('auth_token');

      const response = await apiClient.getCurrentUser();

      if (response.success && response.data?.user) {
        dispatch({ type: 'LOGIN_SUCCESS', payload: { user: response.data.user } });
        return { success: true };
      }

      // Token wurde vom Backend nicht akzeptiert -> Aufraeumen
      localStorage.removeItem('auth_token');
      dispatch({ type: 'LOGIN_FAILURE', payload: response.error || 'Login fehlgeschlagen' });
      return { success: false };
    } catch (error: any) {
      localStorage.removeItem('auth_token');
      dispatch({ type: 'LOGIN_FAILURE', payload: error.message || 'Login fehlgeschlagen' });
      return { success: false };
    }
  };

  const register = async (data: RegisterRequest): Promise<{ success: boolean; requiresVerification?: boolean }> => {
    try {
      dispatch({ type: 'LOGIN_START' });
      
      const response = await apiClient.register(data);
      
      if (response.success) {
        if (response.data?.user && response.data?.token) {
          // Registration with immediate login (rare case)
          dispatch({ type: 'LOGIN_SUCCESS', payload: { user: response.data.user } });
          return { success: true };
        } else {
          // Standard registration - success but requires email verification
          dispatch({ type: 'LOGOUT' }); // Clear loading state without setting error
          return { success: true, requiresVerification: true };
        }
      } else {
        dispatch({ type: 'LOGIN_FAILURE', payload: response.error });
        return { success: false };
      }
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || error.message || 'Registrierung fehlgeschlagen';
      dispatch({ type: 'LOGIN_FAILURE', payload: errorMessage });
      // Re-throw error to preserve response data for component
      throw error;
    }
  };

  const logout = async () => {
    try {
      await apiClient.logout();
    } catch (error) {
      console.warn('Logout request failed:', error);
      // Continue with local logout even if server request fails
    }
    
    // Clear all auth data from both storages
    localStorage.removeItem('auth_token');
    sessionStorage.removeItem('auth_token');
    localStorage.removeItem('user_data');
    localStorage.removeItem('auth_user');
    localStorage.removeItem('admin_selected_role');
    
    dispatch({ type: 'LOGOUT' });
  };

  const updateUser = (user: User) => {
    // If the role is being changed and original role is admin, persist the selection
    if (state.originalRole === 'admin' && user.role !== state.user?.role) {
      dispatch({ type: 'SET_SELECTED_ROLE', payload: user.role });
      // Persist updated user to localStorage for API header
      localStorage.setItem('auth_user', JSON.stringify(user));
    }
    dispatch({ type: 'UPDATE_USER', payload: user });
  };

  const setDemoRole = (role: 'admin' | 'beta' | 'user') => {
    if (state.originalRole !== 'admin' || !state.user) return;

    // Feature-Access-Cache invalidieren, damit Sidebar sofort aktualisiert wird
    invalidateFeatureAccessCache();

    if (role === 'admin') {
      // Zurück zur echten Admin-Rolle
      dispatch({ type: 'SET_SELECTED_ROLE', payload: null });
      dispatch({ type: 'UPDATE_USER', payload: { ...state.user, role: 'admin', isBetaTester: false } });
    } else {
      dispatch({ type: 'SET_SELECTED_ROLE', payload: role });
      dispatch({ type: 'UPDATE_USER', payload: {
        ...state.user,
        role: 'user',
        isBetaTester: role === 'beta',
      }});
    }
  };

  const clearError = () => {
    dispatch({ type: 'CLEAR_ERROR' });
  };

  const refreshUserProfile = useCallback(async () => {
    try {
      const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
      const currentState = stateRef.current;
      if (token && currentState.user) {
        // Fetch fresh user profile from backend
        const response = await apiClient.getUserProfile();
        if (response.success && response.data) {
          // Demo-Rolle beibehalten falls aktiv
          const isDemoActive = currentState.originalRole === 'admin' && currentState.selectedRole && currentState.selectedRole !== 'admin';
          const updatedUser: User = {
            ...currentState.user,
            firstName: response.data.firstName,
            lastName: response.data.lastName,
            email: response.data.email,
            username: response.data.username,
            companyName: response.data.companyName,
            accountType: response.data.accountType,
            address: response.data.address,
            subscription_tier: response.data.subscription_tier,
            role: isDemoActive ? 'user' : response.data.role,
            isBetaTester: isDemoActive ? currentState.selectedRole === 'beta' : response.data.isBetaTester,
            betaJoinedAt: response.data.betaJoinedAt,
            avatarUrl: response.data.avatarUrl,
            // /auth/user/profile liefert `emailVerified` — wir spiegeln das auf
            // das Frontend-Feld `isEmailVerified`. Ohne diese Zeile bleibt der
            // Banner "noch nicht bestätigt" stehen, auch nachdem der User
            // verifiziert hat.
            isEmailVerified: response.data.emailVerified ?? response.data.isEmailVerified ?? currentState.user.isEmailVerified
          };
          dispatch({ type: 'UPDATE_USER', payload: updatedUser });
        }
      }
    } catch (error) {
      console.error('Failed to refresh user profile:', error);
    }
  }, []);

  // Periodically refresh user profile to catch admin changes (every 60 seconds)
  useEffect(() => {
    if (!state.isAuthenticated || !state.user?.id) return;

    const interval = setInterval(async () => {
      await refreshUserProfile();
    }, 60000); // 60 seconds

    return () => clearInterval(interval);
  }, [state.isAuthenticated, state.user?.id, refreshUserProfile]);

  // Subscription Methods — laedt parallel den allgemeinen Status (Subscriptions, hasFcms/hasBitcoin)
  // und den Pflicht-Abo-Status (welche Familien aktiv, welche fehlen, verfügbare Onboarding-Plans).
  const refreshSubscriptions = useCallback(async () => {
    if (!stateRef.current.isAuthenticated) {
      dispatch({ type: 'SUBSCRIPTIONS_ERROR' });
      return;
    }

    try {
      dispatch({ type: 'SUBSCRIPTIONS_LOADING' });
      const [status, requirement] = await Promise.all([
        subscriptionService.getSubscriptionStatus(),
        subscriptionService.getRequirementStatus().catch((err) => {
          console.error('[AuthContext] Failed to load requirement status:', err);
          return null;
        })
      ]);
      // Vor dem Dispatch merken, ob sich der Access-relevante Status
      // tatsächlich geändert hat -- sonst invalidieren wir den
      // Feature-Access-Cache bei JEDEM Background-Refresh (60s-Timer,
      // Fokus-Handler), obwohl sich nichts geändert hat. Das zwingt
      // useFeatureAccess() zu einem Refetch, der FeatureGate kurz auf
      // isLoading setzt und offene Zeichnungen/Modals killt.
      const prevStatus = stateRef.current;
      const accessChanged =
        status.hasFcmsAccess !== prevStatus.hasFcmsAccess ||
        status.hasBitcoinAccess !== prevStatus.hasBitcoinAccess ||
        status.hasRequiredSubscription !== prevStatus.hasRequiredSubscription;

      dispatch({
        type: 'SUBSCRIPTIONS_LOADED',
        payload: {
          subscriptions: status.subscriptions,
          hasFcmsAccess: status.hasFcmsAccess,
          hasBitcoinAccess: status.hasBitcoinAccess,
          // Beide Quellen kombinieren -- /status ist die Wahrheit (kennt Admin-Bypass), /required-status liefert die Plans für die Pflicht-Seite
          hasRequiredSubscription: status.hasRequiredSubscription,
          requirement,
        }
      });
      // Feature-Access-Cache nach Subscription-Refresh leeren — sonst sieht
      // der User noch die alten Paywall-Hinweise (Cache hat 5 min TTL).
      // Fix 2026-05-22: Nach Kauf-Erfolg landet der User auf /checkout/return,
      // dort wird refreshSubscriptions aufgerufen — ohne Cache-Invalidierung
      // bleiben useFeatureAccess()-Hooks bei "kein Zugriff" hängen, bis der
      // User die Seite reloadet.
      // Fix 2026-08-15: NUR noch bei tatsächlicher Änderung invalidieren
      // (siehe accessChanged oben), nicht mehr bei jedem stillen Sync.
      if (accessChanged) {
        invalidateFeatureAccessCache();
      }
    } catch (error) {
      console.error('[AuthContext] Failed to refresh subscriptions:', error);
      dispatch({ type: 'SUBSCRIPTIONS_ERROR' });
    }
  }, []);

  const checkContentAccess = useCallback(async (assetSymbol?: string): Promise<ContentAccess> => {
    try {
      return await subscriptionService.checkContentAccess(assetSymbol);
    } catch (error) {
      console.error('[AuthContext] Failed to check content access:', error);
      // Return restrictive access on error
      return {
        hasAccess: false,
        canAccessAnalysis: false,
        canAccessStrategy: false,
        isTrial: false,
        trialAssetsUsed: 0,
        trialAssetsLimit: null,
        trialDaysRemaining: null,
        requiredPlan: null,
        activeSubscriptions: []
      };
    }
  }, []);

  // Load subscriptions when user authenticates -- nur auf user.id reagieren,
  // damit Profil-Updates (UPDATE_USER) keinen erneuten Subscription-Fetch
  // ausloesen.
  const userId = state.user?.id;
  useEffect(() => {
    if (state.isAuthenticated && userId && !state.isLoading) {
      refreshSubscriptions();
    }
  }, [state.isAuthenticated, userId, state.isLoading, refreshSubscriptions]);

  // Refresh user profile and subscriptions when window regains focus (max alle 2 Min)
  useEffect(() => {
    let lastFocusRefresh = 0;
    const FOCUS_THROTTLE_MS = 2 * 60 * 1000; // 2 Minuten

    const handleFocus = async () => {
      const now = Date.now();
      if (now - lastFocusRefresh < FOCUS_THROTTLE_MS) return;
      lastFocusRefresh = now;

      const current = stateRef.current;
      if (current.isAuthenticated && current.user) {
        await refreshUserProfile();
        await refreshSubscriptions();
      }
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [refreshUserProfile, refreshSubscriptions]);

  const value: AuthContextType = {
    ...state,
    login,
    loginWithToken,
    logout,
    register,
    updateUser,
    clearError,
    refreshUserProfile,
    setDemoRole,
    refreshSubscriptions,
    checkContentAccess,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Hook
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};